import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Code,
  Play,
  TestTube,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Shield,
  Zap,
  Save,
  Upload,
  Download,
  Eye,
  Edit,
  Plus,
  RefreshCw,
  FileText,
  BarChart,
  User,
  Lock
} from 'lucide-react';
import { fetchAgents } from '../lib/api';
import { isProduction } from '../lib/production-guard';

interface CapsuleProject {
  id: string;
  name: string;
  status: 'draft' | 'testing' | 'review' | 'approved' | 'deployed' | 'rejected';
  version: string;
  persona: string;
  createdAt: string;
  updatedAt: string;
  testResults: number;
  approvals: number;
  metadata: {
    category: string;
    tags: string[];
    description: string;
  };
}

interface TestResult {
  testId: string;
  timestamp: string;
  success: boolean;
  deploymentReady: boolean;
  compliance: {
    passed: boolean;
    score: number;
    riskLevel: string;
    violations: any[];
  };
  execution?: {
    success: boolean;
    output: any;
    logs: string[];
    errors: string[];
    warnings: string[];
    performance: {
      executionTime: number;
      memoryUsed: number;
      cpuUsed: number;
    };
  };
  recommendations: string[];
}

interface ComplianceReport {
  developer: {
    id: string;
    clearanceLevel: string;
    certifications: string[];
    reputation: number;
    lastActivity: string;
  };
  compliance: {
    totalViolations: number;
    riskCategory: string;
    recommendedActions: string[];
    nextReviewDate: string;
  };
  permissions: {
    allowedPersonas: string[];
    restrictedFeatures: string[];
    approvalRequired: boolean;
  };
}

export default function CapsuleDevKitPanel() {
  const [activeTab, setActiveTab] = useState('ide');
  const [projects, setProjects] = useState<CapsuleProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<CapsuleProject | null>(null);
  const [currentCode, setCurrentCode] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [complianceReport, setComplianceReport] = useState<ComplianceReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [developerId] = useState('dev_001'); // Mock developer ID
  const [persona, setPersona] = useState('G3X');

  // Fallback projects used when API is unreachable
  const fallbackProjects: CapsuleProject[] = [
    { id: 'module_001', name: 'SecurityAuditModule', status: 'testing', version: '1.2.3', persona: 'G3X', createdAt: '2026-05-01T10:00:00Z', updatedAt: '2026-05-06T14:30:00Z', testResults: 5, approvals: 0, metadata: { category: 'security', tags: ['audit', 'compliance', 'monitoring'], description: 'Automated security audit module for enterprise deployments' } },
    { id: 'module_002', name: 'DataProcessorModule', status: 'draft', version: '1.0.0', persona: 'G4X', createdAt: '2026-05-05T09:15:00Z', updatedAt: '2026-05-06T16:45:00Z', testResults: 2, approvals: 0, metadata: { category: 'data', tags: ['processing', 'transformation', 'analytics'], description: 'High-performance data processing and transformation module' } },
    { id: 'module_003', name: 'NotificationModule', status: 'deployed', version: '2.1.0', persona: 'G2X', createdAt: '2026-04-15T14:20:00Z', updatedAt: '2026-05-04T11:10:00Z', testResults: 12, approvals: 3, metadata: { category: 'communication', tags: ['notifications', 'alerts', 'messaging'], description: 'Enterprise notification and alerting system module' } }
  ];

  // Load real capsule data from API, poll every 15s
  useEffect(() => {
    loadCapsuleData();
    const interval = setInterval(loadCapsuleData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadCapsuleData = async () => {
    try {
      const agentsData = await fetchAgents();

      if (agentsData && agentsData?.agents?.blackBox?.capsules && agentsData?.agents?.blackBox?.capsules.length > 0) {
        const capsules = agentsData?.agents?.blackBox?.capsules;

        // Map capsule modules to CapsuleProject format
        const realProjects: CapsuleProject[] = capsules.map((cap: any, idx: number) => {
          const statusMap: Record<string, CapsuleProject['status']> = {
            active: 'deployed', deployed: 'deployed', testing: 'testing',
            draft: 'draft', review: 'review', approved: 'approved',
            rejected: 'rejected', enabled: 'deployed', disabled: 'draft'
          };
          const rawStatus = (cap.status || 'draft').toLowerCase();

          return {
            id: cap.id || `AGT-${String(idx + 1).padStart(3, '0')}`,
            name: cap.name || cap.id || `Module-${idx + 1}`,
            status: statusMap[rawStatus] || 'draft',
            version: cap.version || '1.0.0',
            persona: cap.persona || cap.owner || 'G3X',
            createdAt: cap.createdAt || cap.created || new Date().toISOString(),
            updatedAt: cap.updatedAt || cap.lastModified || new Date().toISOString(),
            testResults: cap.testResults ?? cap.testCount ?? 0,
            approvals: cap.approvals ?? cap.approvalCount ?? 0,
            metadata: {
              category: cap.category || cap.type || 'general',
              tags: cap.tags || cap.capabilities || [],
              description: cap.description || cap.summary || `Agent module ${cap.id || idx + 1}`
            }
          };
        });

        setProjects(realProjects);
        if (!selectedProject || !realProjects.find(p => p.id === selectedProject.id)) {
          const firstRealProject = realProjects[0];
          if (firstRealProject) {
            setSelectedProject(firstRealProject);
          }
        }
      } else {
        // API returned no capsules -- use fallback (dev only)
        const firstFallbackProject = fallbackProjects[0];
        if (projects.length === 0 && !isProduction() && firstFallbackProject) {
          setProjects(fallbackProjects);
          setSelectedProject(firstFallbackProject);
        }
      }
    } catch (error) {
      console.error('CapsuleDevKitPanel: API fetch failed, using fallback data', error);
      const firstFallbackProject = fallbackProjects[0];
      if (projects.length === 0 && !isProduction() && firstFallbackProject) {
        setProjects(fallbackProjects);
        setSelectedProject(firstFallbackProject);
      }
    }
  };

  // Preserve backward compat: initialize code when selectedProject changes
  useEffect(() => {
    if (selectedProject && !currentCode) {
      initializeCodeForProject(selectedProject);
    }
  }, [selectedProject]);

  const initializeCodeForProject = (project: CapsuleProject) => {
    const varName = project.name.charAt(0).toLowerCase() + project.name.slice(1);
    setCurrentCode(`// ${project.name} - ${project.metadata.description || 'Agentomy Module'}
// Version: ${project.version}
// Author: ${developerId}
// Persona: ${project.persona}

const ${varName} = {
  name: "${project.name}",
  version: "${project.version}",

  async execute(context) {
    console.log("Starting ${project.name} execution...");

    // Security compliance check
    const complianceResult = agentomy.security.checkPermission('module_execute');
    if (!complianceResult) {
      throw new Error('Insufficient permissions for ${project.name}');
    }

    const result = {
      timestamp: Date.now(),
      executor: agentomy.persona.current(),
      moduleId: "${project.id}",
      findings: [],
      score: 0
    };

    // Module logic here
    result.score = 100;

    // Log to TrustChain
    agentomy.trustchain.append({
      type: 'MODULE_EXECUTION',
      data: result,
      executor: agentomy.persona.current()
    });

    console.log(\`${project.name} completed. Score: \${result.score}/100\`);
    return result;
  }
};

return ${varName};`);

    // Initialize compliance report
    setComplianceReport({
      developer: {
        id: developerId,
        clearanceLevel: project.persona === 'G1X' ? 'Analyst' : project.persona === 'G2X' ? 'Builder' : project.persona === 'G3X' ? 'Operator' : project.persona === 'G4X' ? 'Strategist' : project.persona || 'Operator',
        certifications: ['SECURITY_BASIC', 'SECURITY_REVIEW'],
        reputation: 850,
        lastActivity: new Date().toISOString()
      },
      compliance: {
        totalViolations: 2,
        riskCategory: 'LOW',
        recommendedActions: ['Complete advanced security training', 'Review security best practices'],
        nextReviewDate: new Date(Date.now() + 86400000 * 90).toISOString()
      },
      permissions: {
        allowedPersonas: ['Strategist', 'Operator'],
        restrictedFeatures: ['system_override', 'security_bypass'],
        approvalRequired: false
      }
    });
  };

  const handleTestCapsule = async () => {
    setIsLoading(true);
    
    // Mock test execution
    setTimeout(() => {
      const mockResult: TestResult = {
        testId: `test_${Date.now()}`,
        timestamp: new Date().toISOString(),
        success: true,
        deploymentReady: true,
        compliance: {
          passed: true,
          score: 92,
          riskLevel: 'LOW',
          violations: []
        },
        execution: {
          success: true,
          output: {
            timestamp: Date.now(),
            auditor: 'G3X',
            findings: [
              { component: 'ModuleRegistry', status: 'SECURE' },
              { component: 'GovernanceFramework', status: 'COMPLIANT' }
            ],
            score: 100
          },
          logs: [
            'LOG: Starting security audit...',
            'LOG: Security audit completed. Score: 100/100',
            'AUDIT: {"type":"SECURITY_AUDIT","auditor":"G3X"}'
          ],
          errors: [],
          warnings: [],
          performance: {
            executionTime: 245,
            memoryUsed: 1024 * 150, // 150KB
            cpuUsed: 180
          }
        },
        recommendations: [
          'Code quality excellent - ready for production',
          'Consider adding additional error handling for edge cases'
        ]
      };
      
      setTestResult(mockResult);
      setIsLoading(false);
    }, 2000);
  };

  const handleCreateProject = () => {
    const newProject: CapsuleProject = {
      id: `module_${Date.now()}`,
      name: 'NewModule',
      status: 'draft',
      version: '1.0.0',
      persona: persona,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      testResults: 0,
      approvals: 0,
      metadata: {
        category: 'general',
        tags: [],
        description: 'A new Agentomy module'
      }
    };
    
    setProjects(prev => [newProject, ...prev]);
    setSelectedProject(newProject);
    setCurrentCode(`// ${newProject.name} - New Agentomy Module
// Version: ${newProject.version}
// Author: ${developerId}
// Persona: ${persona}

const ${newProject.name.toLowerCase()} = {
  name: "${newProject.name}",
  version: "${newProject.version}",
  
  async execute(context) {
    console.log("Module execution started...");

    // Your module logic here

    return {
      success: true,
      message: "Module executed successfully"
    };
  }
};

return ${newProject.name.toLowerCase()};`);
    setTestResult(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed': return 'text-ag-active bg-green-900 border-green-600';
      case 'approved': return 'text-blue-400 bg-blue-900 border-blue-600';
      case 'review': return 'text-purple-400 bg-purple-900 border-purple-600';
      case 'testing': return 'text-yellow-400 bg-yellow-900 border-yellow-600';
      case 'draft': return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
      case 'rejected': return 'text-ag-critical bg-red-900 border-red-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const getPersonaColor = (persona: string) => {
    switch (persona) {
      case 'G1X': return 'text-red-400 bg-red-900 border-red-600';
      case 'G2X': return 'text-orange-400 bg-orange-900 border-orange-600';
      case 'G3X': return 'text-cyan-400 bg-cyan-900 border-cyan-600';
      case 'G4X': return 'text-green-400 bg-green-900 border-green-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const getRiskColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'CRITICAL': return 'text-ag-critical';
      case 'HIGH': return 'text-orange-400';
      case 'MEDIUM': return 'text-ag-warning';
      case 'LOW': return 'text-ag-active';
      default: return 'text-[#A8A29E]';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
            🧰 Module Development Kit
          </h2>
          <p className="text-[#A8A29E]">Secure module development with governance compliance and sandbox testing</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            className="bg-[#292524] border-[#4A443D] text-white rounded px-3 py-2"
          >
            <option value="G4X">Strategist</option>
            <option value="G3X">Operator</option>
            <option value="G2X">Builder</option>
            <option value="G1X">Analyst</option>
          </select>
          <Badge variant="outline" className={getPersonaColor(persona)}>
            Level {persona === 'G1X' ? 'Analyst' : persona === 'G2X' ? 'Builder' : persona === 'G3X' ? 'Operator' : 'Strategist'} Access
          </Badge>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-4 border-b border-[#3A352F]">
        <button
          onClick={() => setActiveTab('ide')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'ide' 
              ? 'text-cyan-400 border-b-2 border-cyan-400' 
              : 'text-[#A8A29E] hover:text-white'
          }`}
        >
          <Code className="w-4 h-4 inline mr-2" />
          IDE
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'projects' 
              ? 'text-cyan-400 border-b-2 border-cyan-400' 
              : 'text-[#A8A29E] hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Projects
        </button>
        <button
          onClick={() => setActiveTab('compliance')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'compliance' 
              ? 'text-cyan-400 border-b-2 border-cyan-400' 
              : 'text-[#A8A29E] hover:text-white'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Compliance
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'statistics' 
              ? 'text-cyan-400 border-b-2 border-cyan-400' 
              : 'text-[#A8A29E] hover:text-white'
          }`}
        >
          <BarChart className="w-4 h-4 inline mr-2" />
          Statistics
        </button>
      </div>

      {/* IDE Tab */}
      {activeTab === 'ide' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Code Editor */}
          <div className="xl:col-span-2 space-y-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white flex items-center">
                    <Code className="w-5 h-5 mr-2 text-cyan-400" />
                    Code Editor
                    {selectedProject && (
                      <span className="ml-3 text-sm font-normal text-[#A8A29E]">
                        {selectedProject.name} v{selectedProject.version}
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={handleTestCapsule}
                      disabled={isLoading}
                      className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Test
                        </>
                      )}
                    </Button>
                    <Button variant="outline" className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <textarea
                    value={currentCode}
                    onChange={(e) => setCurrentCode(e.target.value)}
                    className="w-full h-96 bg-black border border-[#4A443D] rounded p-4 text-green-400 font-mono text-sm resize-none focus:outline-none focus:border-cyan-400"
                    placeholder="// Write your module code here..."
                    style={{ fontFamily: 'Monaco, Consolas, monospace' }}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                    Lines: {currentCode.split('\n').length} | Chars: {currentCode.length}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Results */}
            {testResult && (
              <Card className="bg-gray-900 border-[#3A352F]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <TestTube className="w-5 h-5 mr-2 text-cyan-400" />
                    Test Results
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 ml-2 text-ag-active" />
                    ) : (
                      <XCircle className="w-5 h-5 ml-2 text-ag-critical" />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Compliance Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-[#A8A29E]">Compliance Score</p>
                      <p className="text-xl font-bold text-green-400">{testResult.compliance.score}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#A8A29E]">Risk Level</p>
                      <p className={`text-lg font-bold ${getRiskColor(testResult.compliance.riskLevel)}`}>
                        {testResult.compliance.riskLevel}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#A8A29E]">Execution Time</p>
                      <p className="text-lg font-bold text-cyan-400">
                        {testResult.execution?.performance.executionTime}ms
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-[#A8A29E]">Memory Used</p>
                      <p className="text-lg font-bold text-purple-400">
                        {Math.round((testResult.execution?.performance.memoryUsed || 0) / 1024)}KB
                      </p>
                    </div>
                  </div>

                  {/* Execution Output */}
                  {testResult.execution && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Execution Output:</h4>
                      <div className="bg-black border border-[#4A443D] rounded p-3">
                        <pre className="text-green-400 text-sm whitespace-pre-wrap">
                          {JSON.stringify(testResult.execution.output, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Logs */}
                  {testResult.execution && testResult.execution.logs.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Execution Logs:</h4>
                      <div className="bg-black border border-[#4A443D] rounded p-3 max-h-32 overflow-y-auto">
                        {testResult.execution.logs.map((log, index) => (
                          <div key={index} className="text-[#A8A29E] text-sm font-mono">
                            {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {testResult.recommendations.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Recommendations:</h4>
                      <ul className="space-y-1">
                        {testResult.recommendations.map((rec, index) => (
                          <li key={index} className="text-[#A8A29E] text-sm flex items-start">
                            <div className="w-2 h-2 bg-[#D4825F] rounded-full mt-2 mr-2 flex-shrink-0"></div>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Deployment Status */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#3A352F]">
                    <span className="text-[#A8A29E]">Deployment Ready:</span>
                    <div className="flex items-center">
                      {testResult.deploymentReady ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-ag-active mr-2" />
                          <span className="text-ag-active font-medium">Ready</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-ag-critical mr-2" />
                          <span className="text-ag-critical font-medium">Not Ready</span>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Selector */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="text-white text-lg">Project</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <Button
                    onClick={handleCreateProject}
                    className="bg-green-600 hover:bg-green-700 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                </div>
                
                {selectedProject && (
                  <div className="space-y-2">
                    <div>
                      <span className="text-[#A8A29E] text-sm">Name:</span>
                      <p className="text-white font-medium">{selectedProject.name}</p>
                    </div>
                    <div>
                      <span className="text-[#A8A29E] text-sm">Status:</span>
                      <Badge className={getStatusColor(selectedProject.status)}>
                        {selectedProject.status}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-[#A8A29E] text-sm">Version:</span>
                      <p className="text-white">{selectedProject.version}</p>
                    </div>
                    <div>
                      <span className="text-[#A8A29E] text-sm">Category:</span>
                      <p className="text-white">{selectedProject.metadata.category}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="text-white text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
                  <TestTube className="w-4 h-4 mr-2" />
                  Run Test Suite
                </Button>
                <Button variant="outline" className="w-full border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
                  <Shield className="w-4 h-4 mr-2" />
                  Security Check
                </Button>
                <Button variant="outline" className="w-full border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
                  <Upload className="w-4 h-4 mr-2" />
                  Submit Review
                </Button>
                <Button variant="outline" className="w-full border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
                  <Download className="w-4 h-4 mr-2" />
                  Export Module
                </Button>
              </CardContent>
            </Card>

            {/* Developer Info */}
            {complianceReport && (
              <Card className="bg-gray-900 border-[#3A352F]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Developer Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#A8A29E] text-sm">Clearance:</span>
                    <Badge className={getPersonaColor(complianceReport.developer.clearanceLevel)}>
                      {complianceReport.developer.clearanceLevel}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#A8A29E] text-sm">Reputation:</span>
                    <span className="text-cyan-400 font-medium">{complianceReport.developer.reputation}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#A8A29E] text-sm">Risk Category:</span>
                    <span className={getRiskColor(complianceReport.compliance.riskCategory)}>
                      {complianceReport.compliance.riskCategory}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#A8A29E] text-sm">Violations:</span>
                    <p className="text-white">{complianceReport.compliance.totalViolations}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Your Module Projects</h3>
            <Button
              onClick={handleCreateProject}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(project => (
              <Card 
                key={project.id} 
                className={`cursor-pointer transition-all ${
                  selectedProject?.id === project.id 
                    ? 'bg-cyan-900/20 border-cyan-500' 
                    : 'bg-gray-900 border-[#3A352F] hover:bg-[#292524]'
                }`}
                onClick={() => setSelectedProject(project)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-white font-medium">{project.name}</h4>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </div>

                  <p className="text-[#A8A29E] text-sm mb-4 line-clamp-2">
                    {project.metadata.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-[#A8A29E]">Version:</span>
                      <div className="text-white font-medium">{project.version}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Persona:</span>
                      <div className="text-white font-medium">{project.persona === 'G1X' ? 'Analyst' : project.persona === 'G2X' ? 'Builder' : project.persona === 'G3X' ? 'Operator' : project.persona === 'G4X' ? 'Strategist' : project.persona}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Tests:</span>
                      <div className="text-white font-medium">{project.testResults}</div>
                    </div>
                    <div>
                      <span className="text-[#A8A29E]">Approvals:</span>
                      <div className="text-white font-medium">{project.approvals}</div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex flex-wrap gap-1">
                      {project.metadata.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="text-xs bg-[#3A352F] text-[#A8A29E] px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                      {project.metadata.tags.length > 3 && (
                        <span className="text-xs text-[#A8A29E]">+{project.metadata.tags.length - 3} more</span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-between items-center text-xs text-[#A8A29E]">
                    <span>Updated {new Date(project.updatedAt).toLocaleDateString()}</span>
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Eye className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && complianceReport && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Security Compliance Report</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Clearance Level</p>
                    <p className="text-2xl font-bold text-cyan-400">{complianceReport.developer.clearanceLevel}</p>
                  </div>
                  <User className="w-8 h-8 text-cyan-400 opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Reputation</p>
                    <p className="text-2xl font-bold text-green-400">{complianceReport.developer.reputation}</p>
                  </div>
                  <Zap className="w-8 h-8 text-green-400 opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Violations</p>
                    <p className="text-2xl font-bold text-ag-warning">{complianceReport.compliance.totalViolations}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-ag-warning opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Risk Category</p>
                    <p className={`text-2xl font-bold ${getRiskColor(complianceReport.compliance.riskCategory)}`}>
                      {complianceReport.compliance.riskCategory}
                    </p>
                  </div>
                  <Shield className="w-8 h-8 opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="text-white">Permissions & Access</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-white font-medium mb-2">Allowed Roles:</h4>
                  <div className="flex flex-wrap gap-2">
                    {complianceReport.permissions.allowedPersonas.map(persona => (
                      <Badge key={persona} className={getPersonaColor(persona)}>
                        {persona}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-white font-medium mb-2">Restricted Features:</h4>
                  <ul className="space-y-1">
                    {complianceReport.permissions.restrictedFeatures.map((feature, index) => (
                      <li key={index} className="text-[#A8A29E] text-sm flex items-center">
                        <Lock className="w-3 h-3 text-ag-critical mr-2" />
                        {feature.replace('_', ' ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="text-white">Recommended Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {complianceReport.compliance.recommendedActions.map((action, index) => (
                    <li key={index} className="text-[#A8A29E] text-sm flex items-start">
                      <div className="w-2 h-2 bg-[#D4825F] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      {action}
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t border-[#3A352F]">
                  <p className="text-[#A8A29E] text-sm">Next Review:</p>
                  <p className="text-white">
                    {new Date(complianceReport.compliance.nextReviewDate).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Development Statistics</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Total Projects</p>
                    <p className="text-2xl font-bold text-cyan-400">{projects.length}</p>
                  </div>
                  <FileText className="w-8 h-8 text-cyan-400 opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Deployed</p>
                    <p className="text-2xl font-bold text-ag-active">
                      {projects.filter(p => p.status === 'deployed').length}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-ag-active opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">In Testing</p>
                    <p className="text-2xl font-bold text-yellow-400">
                      {projects.filter(p => p.status === 'testing').length}
                    </p>
                  </div>
                  <TestTube className="w-8 h-8 text-yellow-400 opacity-60" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#A8A29E]">Draft</p>
                    <p className="text-2xl font-bold text-[#A8A29E]">
                      {projects.filter(p => p.status === 'draft').length}
                    </p>
                  </div>
                  <Edit className="w-8 h-8 text-[#A8A29E] opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="text-white">Project Distribution by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['deployed', 'approved', 'review', 'testing', 'draft'].map(status => {
                  const count = projects.filter(p => p.status === status).length;
                  const percentage = projects.length > 0 ? (count / projects.length) * 100 : 0;
                  
                  return (
                    <div key={status} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A8A29E] capitalize">{status}</span>
                        <span className="text-white">{count} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="w-full bg-[#3A352F] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            status === 'deployed' ? 'bg-green-400' :
                            status === 'approved' ? 'bg-blue-400' :
                            status === 'review' ? 'bg-purple-400' :
                            status === 'testing' ? 'bg-yellow-400' :
                            'bg-gray-400'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}